import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetActionsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import {
  useGetActionsRegister,
  type UseGetActionsRegisterArgs,
} from 'src/hooks/queries/action/useGetActionsRegister';
import { useGetControlsRegister } from 'src/hooks/queries/control/useGetControlsRegister';
import { useLabelledFields as useActionLabelledFields } from 'src/pages/actions/useLabelledFields';
import { useLabelledFields as useControlLabelledFields } from 'src/pages/controls/useLabelledFields';
import type { LabelledIdArray } from 'src/rbac/types';

import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import { UNRATED } from '../../gigawidget/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';
import {
  getActionOverdueLabel,
  ownersField,
  prefixedLabel,
  severitySortedField,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';

export type ControlActionRow = TableRecord & {
  Id: string;
  ControlId: string;
  ControlFriendlyId: string;
  ControlTitle: string;
  ControlType: string;
  DesignEffectiveness: string;
  PerformanceEffectiveness: string;
  OverallEffectiveness: string;
  ActionId: string;
  ActionFriendlyId: string;
  ActionTitle: string;
  // Labelled status excluding the overdue fold — overdue is its own column/getter.
  ActionStatus: string;
  // Labelled priority for table rendering ("High", "Urgent", ...).
  ActionPriority: string;
  // Raw priority value (1 = Urgent ... 4 = Low) — used as categoryGetter key
  // so charts sort by severity rather than alphabetically (RSP-5303).
  ActionPriorityValue: null | number;
  ActionDateDue: string;
  ActionOwners: LabelledIdArray;
  ActionOverdue: string;
};

export default createDataSource<
  ControlActionRow,
  UseGetActionsRegisterArgs,
  GetActionsQuery
>({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Control, Parent_Type_Enum.Action],
  useWidgetQuery: useGetActionsRegister,
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<ControlActionRow>
  ) => {
    const fields = useMemo<TableFields<ControlActionRow>>(
      () => ({
        ControlFriendlyId: simpleField(
          prefixedLabel('control_one', i18n.t('columns.id'))
        ),
        ControlTitle: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.title'))
        ),
        ControlType: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.type'))
        ),
        DesignEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.design_effectiveness')
          )
        ),
        PerformanceEffectiveness: simpleField(
          prefixedLabel(
            'control_one',
            i18n.t('controls.columns.performance_effectiveness')
          )
        ),
        OverallEffectiveness: simpleField(
          prefixedLabel('control_one', i18n.t('controls.columns.effectiveness'))
        ),
        ActionFriendlyId: simpleField(
          prefixedLabel('action_one', i18n.t('columns.id'))
        ),
        ActionTitle: simpleField(
          prefixedLabel('action_one', i18n.t('actions.columns.title'))
        ),
        ActionStatus: simpleField(
          prefixedLabel('action_one', i18n.t('actions.columns.status'))
        ),
        ActionPriority: severitySortedField<ControlActionRow>(
          prefixedLabel('action_one', i18n.t('actions.columns.priority')),
          (item) => item.ActionPriorityValue
        ),
        ActionDateDue: dateColumnFromConfig<ControlActionRow>({
          header: {
            header: prefixedLabel(
              'action_one',
              i18n.t('actions.columns.due_date')
            ),
          },
          dateField: 'ActionDateDue',
        }),
        ActionOwners: ownersField(
          prefixedLabel('action_one', i18n.t('actions.columns.owner')),
          (item) => item.ActionOwners
        ),
        ActionOverdue: simpleField(
          prefixedLabel('action_one', i18n.t('actions.columns.overdue'))
        ),
      }),
      []
    );

    const actionsLabelled = useActionLabelledFields(data?.action);
    const { getLabel: getActionStatusLabel } = useRating('action_status');

    // COI-602 — the join partner must resolve through the SAME permission
    // stack as the primary actions query. A raw Apollo call here was
    // flag-blind, so on a flag-on V3 org one side of this join was decided by
    // perm_v2 and the other by Hasura `role_access`. `queryArgs: {}` reproduces
    // the raw call's variables exactly (entity filter, no dashboard filters);
    // `cache-first` preserves Apollo's own default, which the raw call
    // inherited and `createQueryHook`'s GraphQL leg does not.
    const { data: controlData, loading: controlsLoading } =
      useGetControlsRegister({
        queryArgs: {},
        fetchPolicy: 'cache-first',
      });
    const controlsLabelled = useControlLabelledFields(controlData?.control);

    const joinedData = useMemo<ControlActionRow[] | undefined>(() => {
      if (!actionsLabelled || !controlsLabelled) {
        return undefined;
      }

      const controlMap = new Map(controlsLabelled.map((c) => [c.Id, c]));

      return actionsLabelled.flatMap((action) =>
        action.parents
          .filter(
            (p) =>
              p.parent?.ObjectType === Parent_Type_Enum.Control && p.parent.Id
          )
          .map((parent) => {
            const control = controlMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              ControlId: parent.parent!.Id,
              ControlFriendlyId: control?.SequentialIdLabel ?? '',
              ControlTitle: control?.Title ?? parent.control?.Title ?? '',
              ControlType: control?.Type ?? '',
              DesignEffectiveness: control?.DesignEffectivenessLabelled ?? '',
              PerformanceEffectiveness:
                control?.PerformanceEffectivenessLabelled ?? '',
              OverallEffectiveness: control?.OverallEffectivenessLabelled ?? '',
              ActionId: action.Id,
              ActionFriendlyId: action.SequentialIdLabel ?? '',
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status
                ? (getActionStatusLabel(action.Status) ?? '')
                : '',
              ActionPriority: action.PriorityLabelled ?? '',
              ActionPriorityValue: action.Priority ?? null,
              ActionDateDue: action.DateDue ?? '',
              ActionOwners: action.allOwners,
              ActionOverdue: getActionOverdueLabel(action),
            };
          })
      );
    }, [actionsLabelled, controlsLabelled, getActionStatusLabel]);

    const tableProps = useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('control_action_other'),
      options
    );

    // The join stays undefined until the secondary controls query lands —
    // report that window as loading rather than "no data" (RSP-6539).
    return { ...tableProps, isEnrichmentLoading: controlsLoading };
  },
  entityNamePlural: 'control_action_other',
  entityNameSingular: 'control_action_one',
  fields: 'controlActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'controlFriendlyId',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('columns.id')),
      categoryGetter: (item: ControlActionRow) =>
        item.ControlFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlFriendlyId'),
    },
    {
      id: 'controlTitle',
      fieldId: 'Title',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.title')),
      categoryGetter: (item: ControlActionRow) => item.ControlTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlTitle'),
    },
    {
      id: 'controlType',
      fieldId: 'Type',
      entityGroup: 'control_one',
      name: () => prefixedLabel('control_one', i18n.t('controls.columns.type')),
      categoryGetter: (item: ControlActionRow) => item.ControlType || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ControlType'),
    },
    {
      id: 'designEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.design_effectiveness')
        ),
      categoryGetter: (item: ControlActionRow) =>
        item.DesignEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('DesignEffectiveness'),
      ratingColourKey: 'design_effectiveness',
    },
    {
      id: 'performanceEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel(
          'control_one',
          i18n.t('controls.columns.performance_effectiveness')
        ),
      categoryGetter: (item: ControlActionRow) =>
        item.PerformanceEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('PerformanceEffectiveness'),
      ratingColourKey: 'performance_effectiveness',
    },
    {
      id: 'overallEffectiveness',
      entityGroup: 'control_one',
      name: () =>
        prefixedLabel('control_one', i18n.t('controls.columns.effectiveness')),
      categoryGetter: (item: ControlActionRow) =>
        item.OverallEffectiveness || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('OverallEffectiveness'),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'actionFriendlyId',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('columns.id')),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionFriendlyId'),
    },
    {
      id: 'actionTitle',
      fieldId: 'Title',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('actions.columns.title')),
      categoryGetter: (item: ControlActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
    {
      id: 'actionStatus',
      fieldId: 'Status',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('actions.columns.status')),
      categoryGetter: (item: ControlActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      fieldId: 'Priority',
      entityGroup: 'action_one',
      name: () =>
        prefixedLabel('action_one', i18n.t('actions.columns.priority')),
      // Structured result mirrors the single-entity action source (RSP-5303):
      // the numeric `key` makes pies/legends sort Urgent → High → Normal → Low
      // rather than alphabetically, and the `label` keeps the display readable.
      categoryGetter: (item: ControlActionRow) => {
        if (item.ActionPriorityValue == null) {
          return UNRATED;
        }

        return {
          sortKey: `${item.ActionPriorityValue}`,
          key: item.ActionPriorityValue,
          label: item.ActionPriority || UNRATED,
        };
      },
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'actionDateDue',
      fieldId: 'DateDue',
      entityGroup: 'action_one',
      name: () =>
        prefixedLabel('action_one', i18n.t('actions.columns.due_date')),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionDateDue ? new Date(item.ActionDateDue) : new Date(0),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: { field: 'DateDue', ...toIsoBounds(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActionDateDue'),
    },
    {
      id: 'actionOwner',
      fieldId: 'Owners',
      entityGroup: 'action_one',
      name: () => prefixedLabel('action_one', i18n.t('actions.columns.owner')),
      categoryGetter: (item: ControlActionRow) =>
        item.ActionOwners.map((owner) => ({
          key: owner.id,
          label: owner.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'ActionOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'actionOverdue',
      entityGroup: 'action_one',
      name: () =>
        prefixedLabel('action_one', i18n.t('actions.columns.overdue')),
      categoryGetter: (item: ControlActionRow) => item.ActionOverdue,
      clickthroughFilter: defaultClickthroughFilter('ActionOverdue'),
    },
  ],
});
